<template>
  <div>
    <form @submit.prevent="onSubmit">
      <div class="title">Upload CSV</div>
      <hr>

      <div v-if="showHospital">
        <rp-select
          v-model="resource.hospital_id"
          placeholder="Select Hospital"
          isRequired>
          <option v-for="hospital in hospitals" :key="hospital.id" :value="hospital.id">{{hospital.name}}</option>
        </rp-select>
      </div>
      <div v-if="showLocale">
        <rp-select
          v-model="resource.locale_id"
          placeholder="Select Patient Langauge"
          isRequired>
          <option v-for="locale in locales" :key="locale.id" :value="locale.id">{{locale.name}}</option>
        </rp-select>
      </div>
      <rp-select
        v-model="resource.operation_type_id"
        placeholder="Select Operation Type"
        :error="operation_type_idError"
        @input="touch('operation_type_id')"
        isRequired>
        <option v-for="type in operationTypes" :key="type.id" :value="type.id">{{type.name}}</option>
      </rp-select>

      <div class="file-container" @drop.prevent="fileDropped" @dragover.prevent>
        <input @change="fileLoaded" ref="browseInput" type="file" name="myFile" accept=".csv" isRequired>
        <div v-if="!resource.file" class="file-label" @click="browseFileSystem()">
          Drag and drop CSV file here or 
          <span class="file-button">
            browse
          </span>
        </div>
        <div v-else class="file-uploaded-container" @click="browseFileSystem()">
          <span class="file-name file-label">
            <rp-sprite class="done-icon" name="done"/>
            {{resource.file.name}}
          </span>
          <span class="file-button">
            Replace
          </span>
        </div>
      </div>
      <hr>

      <div class="button-container">
        <div class="check-container">
          <rp-checkbox v-model="resource.terms" />
          <span @click="toggleTerms()">
            By ticking this box you are confirming that all patients within this CSV have consented
            to having their data collected in accordance with the RayPRO consent statement.
          </span>
        </div>
        <rp-button class="create-button" :disabled="isInvalid" type="primary">
          UPLOAD
        </rp-button>
      </div>
    </form>

    <rp-overlay :active="showConfirmationWindow">
      <rp-confirm
        icon="warning_rounded"
        messageText='I confirm that I have read the consent statement to ALL the patients within this file and received their oral consent'
        :confirm="onSubmitOk"
        :cancel="onSubmitCancel" />
    </rp-overlay>

    <rp-overlay :active="showFileErrorWindow">
      <rp-alert
        class="error-icon"
        icon="warning_rounded"
        message="The selected file is not a CSV file"
        :onClick="closeFileErrorWindow" />
    </rp-overlay>

  </div>
</template>

<script>
  import { validationMixin } from 'vuelidate'
  import { required, requiredIf, sameAs } from 'vuelidate/lib/validators'

  import form from '../../../mixins/form'
  import Button from '../../../components/ui/inputs/Button'
  import Select from '../../../components/ui/inputs/StyledSelect'
  import Checkbox from '../../../components/ui/inputs/Checkbox'  
  import Sprite from '../../../components/ui/Sprite'
  import Overlay from '../../../components/ui/Overlay'
  import Alert from '../../../components/ui/Alert'
  import Confirm from '../../../components/ui/DeleteConfirm'
  import {errorMessages} from '../../../api/error_messages'

  export default {
    name: 'OperationUploadForm',
    mixins: [validationMixin, form],

    components: {
      'rp-button': Button,
      'rp-select': Select,
      'rp-checkbox': Checkbox,
      'rp-sprite': Sprite,
      'rp-overlay': Overlay,
      'rp-alert': Alert,
      'rp-confirm': Confirm
    },

    data() {
      return {
        showFileErrorWindow: false,
        showConfirmationWindow: false,
      }
    },

    computed: {
      ...errorMessages(['operation_type_id']),
    },

    props: {
      hospitals: Array,
      locales: Array,
      operationTypes: Array,
      showHospital: Boolean,
      showLocale: Boolean
    },

    methods: {
      fileLoaded(event) {
        this.setActiveFile(event.target.files[0])
      },

      fileDropped(event) {
        this.setActiveFile(event.dataTransfer.files[0])
      },

      setActiveFile(file) {
        if (file.type.includes("csv") || file.name.toLowerCase().endsWith('.csv')) {
          this.resource.file = file
        } else {
          this.showFileErrorWindow = true
        }
      },

      browseFileSystem() {
        this.$refs.browseInput.value = null
        this.$refs.browseInput.click()
      },

      onSubmit(event) {
        event.preventDefault()
        this.showConfirmationWindow = true
      },

      onSubmitOk() {
        this.showConfirmationWindow = false
        this.submit()
      },

      onSubmitCancel() {
        this.showConfirmationWindow = false
      },

      closeFileErrorWindow() {
        this.showFileErrorWindow = false
      },

      toggleTerms() {
        if (this.resource.terms == undefined) {
          this.$set(this.resource, "terms", false)
        }
        this.resource.terms = !this.resource.terms
      }
    },

    validations: {
      resource: {
        file: {required},
        terms: {sameAs: sameAs(function(n) {
          return true
        })},
        hospital_id: {required: requiredIf(function(n) {
          return this.showHospital
        })},
        locale_id: {required: requiredIf(function(n) {
          return this.showLocale
        })},
        operation_type_id: {required},
      }
    },
  }
</script>

<style lang="sass" scoped>
  @import '../../../styles/main.sass'
  
  form
    display: flex
    flex-direction: column
    margin: 0 20px
    position: relative
    width: 90vw
    max-width: 550px
    padding-bottom: 10px
    transition: opacity 0.2s

    .title
      color: #0C3717
      font-size: 1.8rem
      font-weight: 400
      margin-bottom: 20px

    hr
      margin: 0 -30px
      background-color: #d4d4d4
      height: 1px;
      border: 0

    .file-container
      border: 1px dashed grey;
      display: flex
      justify-content: center
      align-items: center
      padding: 40px 30px
      width: 100%
      margin: 15px 0
      min-height: 150px;

      input
        display: none

      .file-label
        font-size: 1.3rem
        color: #0C3717
        cursor: pointer

    .file-button
      text-decoration: underline
      padding: 0
      color: #0C3717
      font-size: 1.3rem
      font-weight: 200
      cursor: pointer

    .file-uploaded-container
      display: flex
      flex-direction: column
      align-items: center

      .file-name
        font-size: 1.6rem
        text-align: center

        .done-icon
          width: 24px
          height: 24px
          fill: #8BC34A
          margin-bottom: -6px

      .file-button
        margin-top: 10px
        color: #848484

    .button-container
      display: flex
      justify-content: space-between

      .check-container
        margin-top: 5px
        display: flex
        align-items: center

        span
          cursor: pointer

      button
        padding: 12px 50px
        margin-top: 15px
        height: 40px

        @media screen and (max-width: $screen-small)
          padding: 12px 20px

        &.create-button
          color: white

</style>



// WEBPACK FOOTER //
// ../raypro_ui/src/components/ui/forms/OperationUpload.vue